import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { FINAL_GAME_STATS, STRESS_BALANCED } from "./stage2PrinciplesData";

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    return String(u?.userId ?? u?.userid ?? u?.id ?? "").trim();
  } catch {
    return "";
  }
}

export default function Stage2FinalComplete({
  productivityScore,
  onContinue,
  submitting = false,
}) {
  const adminToken = useSelector(selectAdminToken);
  const [rank, setRank] = useState(null);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    let cancelled = false;
    async function loadRank() {
      const uid = readSessionUserId();
      if (!adminToken || !backendBase || !uid) return;
      try {
        const res = await fetch(`${backendBase}/getLeaderboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: uid, limit: 50 }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success && Array.isArray(data.data)) {
          const row = data.data.find(
            (r) => String(r.userId ?? r.userid ?? "") === uid
          );
          if (row?.rank != null) setRank(Number(row.rank));
        }
      } catch {
        /* ignore */
      }
    }
    loadRank();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase]);

  return (
    <div
      className="stage1-modal-overlay stage2-modal-overlay stage2-modal-overlay--final"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stage2-final-title"
    >
      <div className="stage1-modal stage1-modal--progress stage2-final-modal">
        <div className="stage1-modal__hero" aria-hidden>
          <span className="stage1-modal__hero-ring" />
          <span className="stage1-modal__hero-icon">✓</span>
        </div>

        <h2 id="stage2-final-title" className="stage1-modal__title stage2-modal__title">
          You Escaped the Grind
        </h2>
        <p className="stage1-modal__badge">Productivity framework complete</p>

        <div
          className="stage1-stress stage1-stress--horizontal stage2-final__stress"
          role="meter"
          aria-valuenow={STRESS_BALANCED}
          aria-label="Stress meter balanced"
        >
          <span className="stage1-stress__heading">Stress Meter</span>
          <div className="stage1-stress__track">
            <div
              className="stage1-stress__fill stage2-final__stress-fill"
              style={{ width: `${STRESS_BALANCED}%` }}
            />
          </div>
          <span className="stage1-stress__value">Balanced</span>
        </div>

        <dl className="stage2-final__stats">
          <div className="stage2-final__stat">
            <dt>Productivity Score</dt>
            <dd>{productivityScore}</dd>
          </div>
          <div className="stage2-final__stat">
            <dt>Tasks Prioritized</dt>
            <dd>{FINAL_GAME_STATS.tasksPrioritized}</dd>
          </div>
          <div className="stage2-final__stat">
            <dt>Emails Managed</dt>
            <dd>{FINAL_GAME_STATS.emailsManaged}</dd>
          </div>
          <div className="stage2-final__stat">
            <dt>Meetings Optimized</dt>
            <dd>{FINAL_GAME_STATS.meetingsOptimized}</dd>
          </div>
          <div className="stage2-final__stat">
            <dt>Principles Found</dt>
            <dd>{FINAL_GAME_STATS.principlesFound}</dd>
          </div>
          <div className="stage2-final__stat stage2-final__stat--wide">
            <dt>Leaderboard Rank</dt>
            <dd>{rank != null ? `#${rank}` : "—"}</dd>
          </div>
        </dl>

        <div className="stage1-modal__quote stage2-final__quote">
          <p>You didn&apos;t escape work.</p>
          <p>You escaped the chaos around work.</p>
          <p>Prioritize wisely. Focus intentionally. Make time for what matters.</p>
        </div>

        <button
          type="button"
          className="stage1-modal__cta stage1-modal__cta--arrow"
          disabled={submitting}
          onClick={onContinue}
        >
          <span>{submitting ? "Saving…" : "View Leaderboard"}</span>
          {!submitting ? (
            <span className="stage1-modal__cta-icon" aria-hidden>
              →
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
